// hooks/useBreadcrumbs.ts
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { breadcrumbRoutes, IBreadcrumbRoute } from './BreadcrumbConfig';
import { useDrawer } from '../components/app/DrawerContext';


export interface IBreadcrumbItem {
  name: string;
  path: string;
  isActive: boolean;
}


export const useBreadcrumbs = () => {
  const location = useLocation();
  const params = useParams();
  const { showAllGroups, setShowAllGroups } = useDrawer();
  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbItem[]>([]);

  const generateBreadcrumbs = () => {
    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);

    let breadcrumbItems: IBreadcrumbItem[] = [
      { name: 'Group', path: '/groups', isActive: false },
      { name: 'Home', path: '/services', isActive: false }
    ];

    // Find exact match first
    let matchedRoute = breadcrumbRoutes.find((route: any) => {
      if (route.dynamic) {
        const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(currentPath);
      }
      return route.path === currentPath;
    });

    if (matchedRoute) {
      // Build breadcrumb chain using parent relationships
      const breadcrumbChain: IBreadcrumbRoute[] = [];
      let currentRoute: IBreadcrumbRoute | undefined = matchedRoute;

      while (currentRoute) {
        breadcrumbChain.unshift(currentRoute);
        if (currentRoute.parent) {
          currentRoute = breadcrumbRoutes.find((route: any) => route.path === currentRoute!.parent);
        } else {
          currentRoute = undefined;
        }
      }

      // Convert to breadcrumb items
      breadcrumbItems = [
        { name: 'Group', path: '/services', isActive: false },
        { name: 'Home', path: '/services', isActive: false },
        ...breadcrumbChain.map((route, index) => {
          let displayPath = route.path;
          let displayName = route.name;

          // Handle dynamic routes
          if (route.dynamic && params) {
            Object.keys(params).forEach((param) => {
              displayPath = displayPath.replace(`:${param}`, params[param] || '');

              // Customize display name for specific dynamic routes
              if (route.path.includes(':id') && params.id) {
                displayName = `${route.name} #${params.id}`;
              }
              if (route.path.includes(':name') && params.name) {
                displayName = params.name;
              }
            });
          }

          return {
            name: displayName,
            path: displayPath,
            isActive: index === breadcrumbChain.length - 1
          };
        })
      ];
    } else {
      // Fallback: Generate breadcrumbs from path segments
      let currentPath = '';
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const route = breadcrumbRoutes.find((r: any) => r.path === currentPath);

        if (route) {
          breadcrumbItems.push({
            name: route.name,
            path: currentPath,
            isActive: index === pathSegments.length - 1
          });
        } else {
          // Fallback to segment name
          breadcrumbItems.push({
            name: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
            path: currentPath,
            isActive: index === pathSegments.length - 1
          });
        }
      });
    }

    setBreadcrumbs(breadcrumbItems);
  };

  useEffect(() => {
    generateBreadcrumbs();
  }, [location, params]);

  return breadcrumbs;
};
